import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import secureLocalStorage from "react-secure-storage";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { loginSuccess } from "../../features/auth/authSlice";
import Logo from "../../assets/images/logo.png";

const css = `
  @import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600&display=swap");

  .al {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f7f7f7;
    font-family: "Outfit", sans-serif;
    padding: 24px;
  }

  .al__box {
    background: #ffffff;
    border: 1px solid #ebebeb;
    border-radius: 20px;
    padding: 48px 44px;
    width: 100%;
    max-width: 400px;
  }

  .al__logo {
    height: 32px;
    width: auto;
    object-fit: contain;
    display: block;
    margin-bottom: 36px;
  }

  .al__title {
    font-size: 20px;
    font-weight: 600;
    color: #080808;
    margin: 0 0 4px;
  }

  .al__subtitle {
    font-size: 13px;
    font-weight: 400;
    color: #999999;
    margin: 0 0 32px;
  }

  .al__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .al__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .al__label {
    font-size: 13px;
    font-weight: 500;
    color: #444444;
  }

  .al__input-wrap {
    display: flex;
    align-items: center;
    background: #f4f4f4;
    border: 1.5px solid #e4e4e4;
    border-radius: 10px;
    transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  }
  .al__input-wrap:focus-within {
    border-color: #1971c2;
    box-shadow: 0 0 0 3px rgba(25,113,194,0.1);
    background: #ffffff;
  }
  .al__input-wrap--error {
    border-color: #e03131 !important;
    box-shadow: 0 0 0 3px rgba(224,49,49,0.08) !important;
  }

  .al__prefix {
    font-size: 13px;
    font-weight: 500;
    color: #888888;
    padding: 0 4px 0 14px;
    flex-shrink: 0;
  }

  .al__input {
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-family: "Outfit", sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #444444;
    padding: 11px 14px;
    min-width: 0;
  }
  .al__input::placeholder { color: #bbbbbb; }
  .al__input[type="number"]::-webkit-outer-spin-button,
  .al__input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; }
  .al__input[type="number"] { -moz-appearance: textfield; }

  .al__pw-toggle {
    background: transparent;
    border: none;
    padding: 0 12px;
    color: #aaaaaa;
    cursor: pointer;
    display: flex;
    align-items: center;
    flex-shrink: 0;
    transition: color 0.25s ease;
  }
  .al__pw-toggle:hover { color: #444444; }

  .al__field-error {
    font-size: 12px;
    color: #e03131;
    margin: 0;
  }

  .al__api-error {
    font-size: 13px;
    color: #c92a2a;
    background: rgba(224,49,49,0.06);
    border: 1px solid rgba(224,49,49,0.15);
    border-radius: 8px;
    padding: 10px 14px;
  }

  .al__submit {
    margin-top: 4px;
    width: 100%;
    padding: 12px;
    border-radius: 10px;
    border: none;
    background: #1971c2;
    color: #ffffff;
    font-family: "Outfit", sans-serif;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background 0.25s ease;
  }
  .al__submit:hover:not(:disabled) { background: #1864ab; }
  .al__submit:disabled { opacity: 0.6; cursor: not-allowed; }

  .al__spinner { animation: al-spin 0.8s linear infinite; }
  @keyframes al-spin { to { transform: rotate(360deg); } }

  @media (max-width: 480px) {
    .al__box { padding: 36px 24px; }
  }
`;

const AdminLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ mobile: "", password: "" });

  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const savedAuth = secureLocalStorage.getItem("auth");
    if (savedAuth?.token) {
      dispatch(loginSuccess(savedAuth));
      navigate("/dashboard/myspace", { replace: true });
    }
  }, [dispatch, navigate]);

  const validate = () => {
    const errors = { mobile: "", password: "" };
    let valid = true;
    if (!mobile || !/^\d{10}$/.test(mobile)) {
      errors.mobile = "Enter a valid 10-digit mobile number.";
      valid = false;
    }
    if (!password || password.length < 6) {
      errors.password = "Password must be at least 6 characters.";
      valid = false;
    }
    setFieldErrors(errors);
    return valid;
  };

  const handleMobileChange = (e) => {
    const val = e.target.value.replace(/\D/g, "");
    if (val.length <= 10) setMobile(val);
    setFieldErrors((p) => ({ ...p, mobile: "" }));
    setApiError("");
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setFieldErrors((p) => ({ ...p, password: "" }));
    setApiError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    setApiError("");
    try {
      const { data } = await axios.post(
        `${baseUrl}/all/admin/login`,
        { mobile, password },
        { headers: { "Content-Type": "application/json" } }
      );
      if (data?.statusType === "SUCCESS") {
        dispatch(loginSuccess(data));
        secureLocalStorage.setItem("auth", data);
        secureLocalStorage.setItem("token", data.token);
        secureLocalStorage.setItem("refreshToken", data.refreshToken);
        navigate("/user", { replace: true });
      } else {
        setApiError(data?.text || "Login failed. Please check your credentials.");
      }
    } catch (err) {
      setApiError(
        err.response?.data?.text ||
        (err.response?.status === 401
          ? "Invalid mobile number or password."
          : "Something went wrong. Please try again.")
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{css}</style>
      <div className="al">
        <div className="al__box">
          <img src={Logo} alt="All Home Desire" className="al__logo m-auto mb-4" />
          <form className="al__form" onSubmit={handleSubmit} noValidate>

            <div className="al__field">
              <label className="al__label">Mobile Number</label>
              <div className={`al__input-wrap${fieldErrors.mobile ? " al__input-wrap--error" : ""}`}>
                <span className="al__prefix">+91</span>
                <input
                  className="al__input"
                  type="number"
                  placeholder="10-digit number"
                  value={mobile}
                  onChange={handleMobileChange}
                  inputMode="numeric"
                  autoComplete="tel"
                />
              </div>
              {fieldErrors.mobile && <p className="al__field-error">{fieldErrors.mobile}</p>}
            </div>

            <div className="al__field">
              <label className="al__label">Password</label>
              <div className={`al__input-wrap${fieldErrors.password ? " al__input-wrap--error" : ""}`}>
                <input
                  className="al__input"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={handlePasswordChange}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="al__pw-toggle"
                  onClick={() => setShowPassword((p) => !p)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              {fieldErrors.password && <p className="al__field-error">{fieldErrors.password}</p>}
            </div>

            {apiError && <div className="al__api-error">{apiError}</div>}

            <button type="submit" className="al__submit" disabled={isLoading}>
              {isLoading
                ? <><Loader2 size={16} className="al__spinner" /> Signing in…</>
                : "Sign In"
              }
            </button>

          </form>
        </div>
      </div>
    </>
  );
};

export default AdminLogin;